import React, { useState } from "react";
import Layout from "../../layout/Layout";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { BsGridFill } from "react-icons/bs";
import { FaQrcode, FaRegListAlt } from "react-icons/fa";
import { FiMessageSquare, FiSettings, FiUser } from "react-icons/fi";
import { RiLockPasswordLine, RiLogoutCircleLine } from "react-icons/ri";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { HiOutlineBookmark } from "react-icons/hi";
import { MdCardGiftcard } from "react-icons/md";
import { TbClipboardList } from "react-icons/tb"; // Tabler Icons

import {
  MdDashboard,
  MdCalendarMonth,
  MdShoppingCart,
  MdPerson,
  MdQrCode,
  MdMessage,
  MdBookmarkBorder,
  MdLockOutline,
} from "react-icons/md";

import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"; // Add this icon
import { Disclosure, Transition } from "@headlessui/react";

function SideBar({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // <- new

  const logoutHandler = () => {
    // dispatch(logout());
    toast.success("Logged out successfully");
    navigate("/");
  };

  const SideLinkGroups = [
    {
      id: "business",
      group: "Service History",
      items: [
        { name: "Dashboard", link: "/dashboard", icon: MdDashboard },
        { name: "Appointments", link: "/booking", icon: MdCalendarMonth },
        { name: "Ordering", link: "/orders", icon: TbClipboardList },
        { name: "Loyalty Program", link: "/loyalty", icon: MdCardGiftcard },
      ],
    },
    {
      id: "account",
      group: "Account Settings",
      items: [
        { name: "Update Profile", link: "/account", icon: FiUser },
        { name: "QR code", link: "/qr-code", icon: FaQrcode },
        { name: "Message", link: "/userInbox", icon: FiMessageSquare },
        {
          name: "Change Password",
          link: "/account-password",
          icon: RiLockPasswordLine,
        },
      ],
    },
    {
      id: "saved",
      group: "Saved",
      items: [
        {
          name: "Save List",
          link: "/account-savelists",
          icon: HiOutlineBookmark,
        },
      ],
    },
  ];

  const active = "bg-redlight text-reddark";
  const hover = "hover:text-redlight hover:bg-deepGray hover:bg-gray-700";
  const inActive =
    "rounded font-medium text-sm transitions flex gap-3 items-center p-4";

  const [openGroups, setOpenGroups] = useState(() => {
    // All groups open by default
    return SideLinkGroups.reduce((acc, group) => {
      acc[group.id] = true;
      return acc;
    }, {});
  });

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const [openGroup, setOpenGroup] = useState("business"); // default open

  React.useEffect(() => {
    for (const group of SideLinkGroups) {
      if (group.items.some((item) => location.pathname.startsWith(item.link))) {
        setOpenGroup(group.id);
        break;
      }
    }
  }, [location.pathname]);

  return (
    <Layout header={true}>
      <div className="bg-gray-900 text-white pb-8 pt-12 px-4">
        <div className="min-h-screen container mx-auto px-2">
          <div className="xl:grid grid-cols-8 gap-10 items-start md:py-12 py-6">
            <div className="sticky col-span-2 bg-gray-800 p-6 rounded-md xl:mb-0 mb-5">
              {SideLinkGroups.map((section) => {
                const isOpen = openGroup === section.id;

                return (
                  <div key={section.id} className="mb-4">
                    <button
                      onClick={() => setOpenGroup(isOpen ? null : section.id)}
                      className="flex items-center justify-between w-full px-4 py-2 text-sm font-semibold text-gray-400 uppercase tracking-wider hover:text-white"
                    >
                      {section.group}
                      <ChevronDownIcon
                        className={`w-4 h-4 text-redlight transform transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <Transition
                      show={isOpen}
                      enter="transition-all duration-300 ease-out"
                      enterFrom="opacity-0 max-h-0"
                      enterTo="opacity-100 max-h-[500px]"
                      leave="transition-all duration-200 ease-in"
                      leaveFrom="opacity-100 max-h-[500px]"
                      leaveTo="opacity-0 max-h-0"
                    >
                      <div className="mt-1 overflow-hidden">
                        {section.items.map((item, i) => (
                          <NavLink
                            key={i}
                            to={item.link}
                            end={item.link === "/dashboard"}
                            className={({ isActive }) =>
                              `${inActive} ${
                                isActive ? active : hover
                              } text-white`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <item.icon
                                  className={`text-lg ${
                                    isActive ? "text-white" : "text-redlight"
                                  } transition-colors`}
                                />
                                <p>{item.name}</p>
                              </>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    </Transition>
                  </div>
                );
              })}

              <button
                onClick={logoutHandler}
                className={`${inActive} ${hover} w-full text-white hover:bg-gray-700`}
              >
                <RiLogoutCircleLine className="text-lg" />
                <p>Log Out</p>
              </button>
            </div>

            <div
              data-aos="fade-up"
              data-aos-offset="200"
              data-aos-delay="10"
              data-aos-duration="1000"
              className="col-span-6 rounded-md bg-gray-800 p-6"
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SideBar;
